import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import { OtherPrivilegeGuard } from "src/utils/authorization/OtherPrivilegeGuard";
import { UserOtherPrivilegesEnum } from "src/modules/auth/userOtherPrivilegesSlice";
import ByzMuiButton from "src/components/core-components/mui/buttons/ByzMuiButton";
import { InputAdornment } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import RadioGroup from '@mui/material/RadioGroup';
import {
  GridToolbar,
  GridColDef,
  GridPaginationModel,
  GridToolbarContainer,
} from "@mui/x-data-grid-premium";
import FormControlLabel from '@mui/material/FormControlLabel';
import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";
import Radio from '@mui/material/Radio';
import SearchIcon from '@mui/icons-material/Search';
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import {
  Box,
  Grid,
} from "@mui/material";
import { useTranslate } from "src/locales";
import { useNavigate } from "react-router-dom";
import { fetchOpenedMedcardsRows } from "./openedMedcardsFetch";
import {
  OPENED_MEDCARDS_DEFAULT_PAGE_SIZE,
  OPENED_MEDCARDS_PAGE_SIZE_OPTIONS,
} from "./openedMedcardsGridConfig";
import { MenuModalProps, MedcardRow } from "src/models/OpenedMedcars";
import ByzMuiPrintButton from "src/components/core-components/mui/buttons/ByzMuiPrintButton";
import { MedcardGpdIds } from "src/enums/MedcardGpdIds";
import { MedcardPdfViewer } from "./MedcardPdfViewer";

function CustomToolbar() {
  return (
    <GridToolbarContainer sx={{ color: "#00B8D9", p: 0.5, ml: -1 }}>
      <GridToolbar />
    </GridToolbarContainer>
  );
}

export const OpenedMedcards = (props: MenuModalProps) => {
  const [inputSearchValue, setInputSearchValueValue] = useState("");
  const [lab, setLab] = useState(false);
  const [radiology, setRadiology] = useState(false);
  const [examination, setExamination] = useState(false);
  const [other, setOther] = useState(false);
  const [patientFilterType, setPatientFilterType] = useState("1");
  const [rows, setRows] = useState<MedcardRow[]>([]);
  const [loading, setLoading] = useState(false);
  
  const [selectedReportRow, setSelectedReportRow] = useState<MedcardRow | null>(null);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: OPENED_MEDCARDS_DEFAULT_PAGE_SIZE,
  });

  const isPdfLoadingRef = useRef(false);

  const patientId = props.ptId;
  const { t } = useTranslate();
  const navigate = useNavigate();

  const handleLoadingChange = useCallback((loading: boolean) => {
    setIsPdfLoading(loading);
    isPdfLoadingRef.current = loading;
  }, []);

  const handleOpenCard = useCallback(async (row: MedcardRow) => {
    if (isPdfLoadingRef.current) return;

    const isReportCard = row.gpdid === MedcardGpdIds.Lab || row.gpdid === MedcardGpdIds.Radiology || row.gpdid === MedcardGpdIds.Examination;

    if (isReportCard) {
      isPdfLoadingRef.current = true;
      setIsPdfLoading(true); 
      setSelectedReportRow(row);
      setRefreshKey(prev => prev + 1); 
    } else {
      props.onClose();
      if (props.onNavigate) {
        props.onNavigate();
      }

      setTimeout(() => {
        navigate(row.url);
      }, 100);
    }
  }, [props, navigate]);

  const columns = useMemo<GridColDef[]>(() => [
    {
      field: "Open",
      headerName: t("opened-medcards.open"),
      renderCell(params) {
        return (
          <OtherPrivilegeGuard
            uopmid={UserOtherPrivilegesEnum.MedicalCards}
            privid={params.row.orId}
            fallback={
              <ByzMuiButton
                sx={{
                  opacity: 0.5,
                  width: '100%',
                  height: '30px', 
                  minHeight: '30px',
                  padding: '2px 8px'
                }}
                variant="contained"
                color="primary"
                disabled
                onClick={() => {}}
              >
                {t("opened-medcards.open")}
              </ByzMuiButton>
            }
          >
            <ByzMuiButton
              onClick={() => handleOpenCard(params.row)}
              disabled={isPdfLoading}
              sx={{
                width: "100%",
                height: '30px', 
                minHeight: '30px',
                padding: '2px 8px',
              }}
              variant="contained"
              color="primary"
            >
                {t("opened-medcards.open")}
            </ByzMuiButton>
          </OtherPrivilegeGuard>
        );
      },
      width: 90,
    },
    { field: "CardName", headerName: t("opened-medcards.card-name"), flex: 2 },
    { field: "Status", headerName: t("opened-medcards.status"), flex: 1 },
    { field: "ProcessTime", headerName: t("opened-medcards.process-time"), flex: 1 },
  ], [isPdfLoading, t, handleOpenCard]);

  const fetchPatientMedcards = useCallback(async () => {
    if (!patientId) return;

    setLoading(true);
    try {
      const formattedRows = await fetchOpenedMedcardsRows(
        patientId,
        patientFilterType,
        props.upn,
        props.gender,
        t
      );
      setRows(formattedRows);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [patientId, patientFilterType, props.upn, props.gender, t]);

  // Reset filters when the modal opens or the patient changes
  useEffect(() => {
    if (props.open && patientId) {
      setInputSearchValueValue("");
      setPatientFilterType("1");
      setRadiology(false);
      setExamination(false);
      setOther(false);
      setLab(false);
      setSelectedReportRow(null); // Reset selected report
      setPaginationModel({ page: 0, pageSize: OPENED_MEDCARDS_DEFAULT_PAGE_SIZE });
    }
  }, [props.open, patientId]);

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [inputSearchValue, lab, radiology, examination, other, patientFilterType]);

  useEffect(() => {
    if (props.open && patientId) {
      fetchPatientMedcards();
    }
  }, [props.open, patientId, patientFilterType]);

  const getFilteredRows = () => {
    let filtered = [...rows];

    if (inputSearchValue.length >= 2) {
      filtered = filtered.filter(row =>
        row.CardName.toLowerCase().includes(inputSearchValue.toLowerCase()) ||
        row.Status.toLowerCase().includes(inputSearchValue.toLowerCase())
      );
    }

    if (lab || radiology || examination || other) {
      filtered = filtered.filter(row => {
        const gpdid = row.gpdid;

        const matchesLab = lab && gpdid === MedcardGpdIds.Lab;
        const matchesRadiology = radiology && gpdid === MedcardGpdIds.Radiology;
        const matchesExamination = examination && gpdid === MedcardGpdIds.Examination;
        const matchesOther = other && (gpdid === null || (gpdid !== MedcardGpdIds.Lab && gpdid !== MedcardGpdIds.Radiology && gpdid !== MedcardGpdIds.Examination));

        return matchesLab || matchesRadiology || matchesExamination || matchesOther;
      });
    }

    return filtered;
  };

  return (
    <OpenComponentDialog
      title={t("opened-medcards.openedcards")}
      open={props.open}
      onClose={props.onClose}
      isMedCard={true}
      isOpenMedCard={false}
      rowID={patientId}
    >
      <Box sx={{ height: '75vh', width: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'flex-start',
            gap: 2,
            flexWrap: 'wrap'
        }}>
            <Box sx={{ flex: 1, minWidth: '200px', maxWidth: '400px' }}>
                <ByzMuiTextField
                    placeholder={t("medcard") + " " + t("search")}
                    type="search"
                    variant="outlined"
                    fullWidth
                    size="small"
                    onChange={(e) => setInputSearchValueValue(e.target.value)}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        sx: {
                            borderRadius: '16px',
                            backgroundColor: '#f4f7fe',
                            "& fieldset": { border: "none" },
                            ml: 2
                        },
                    }}
                />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', ml: 4 }}>
                <FormControlLabel
                    control={
                        <RadioGroup
                            row
                            value={patientFilterType}
                            onChange={(e) => setPatientFilterType(e.target.value)}
                        >
                            <FormControlLabel value="1" control={<Radio size="small" />} label={t("opened-medcards.arrivals")} />
                            <FormControlLabel value="0" control={<Radio size="small" />} label={t("opened-medcards.allarrivals")} />
                        </RadioGroup>
                    }
                    label=""
                    sx={{ m: 0, mr: 2 }}
                />

                <Box sx={{ display: 'flex', gap: 0.5, ml: 2 }}>
                    <FormControlLabel control={<Checkbox size="small" checked={lab} onChange={() => setLab(!lab)} />} label={t("opened-medcards.laboratory")} />
                    <FormControlLabel control={<Checkbox size="small" checked={radiology} onChange={() => setRadiology(!radiology)} />} label={t("opened-medcards.radiology")} />
                    <FormControlLabel control={<Checkbox size="small" checked={examination} onChange={() => setExamination(!examination)} />} label={t("opened-medcards.inspection")} />
                    <FormControlLabel control={<Checkbox size="small" checked={other} onChange={() => setOther(!other)} />} label={t("opened-medcards.other")} />
                </Box>

                <Box sx={{ borderLeft: '1px solid #ccc', pl: 2, ml:2 }}>
                    <ByzMuiPrintButton disabled type={1} sx={{ ml: 1, width: '36px', height: '36px', minWidth: '36px', padding: "5px" }} />
                </Box>
            </Box>
        </Box>

        <Grid container columnSpacing={2} rowSpacing={1} sx={{ flexGrow: 1, m: 0, height: 'calc(100% - 60px)', overflow: 'hidden' }}>
          <Grid item xs={12} md={5} sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            pr: 1
          }}>
            <Box sx={{ flexGrow: 1, width: '100%', overflow: 'hidden' }}>
              <ByzMuiDataGrid
                rows={getFilteredRows()}
                customToolbar={CustomToolbar}
                columns={columns}
                paginationMode="client"
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[...OPENED_MEDCARDS_PAGE_SIZE_OPTIONS]}
                getRowId={(row) => row.id}
                loading={loading}
                sx={{ height: '100%' }}
                initialState={{ density: 'compact' }}
                whiteGrid
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={7} sx={{ height: '100%', p: '0 !important', position: 'relative' }}>
             <MedcardPdfViewer 
                selectedRow={selectedReportRow} 
                onLoadingChange={handleLoadingChange}
                refreshKey={refreshKey}
             />
          </Grid>
        </Grid>
      </Box>
    </OpenComponentDialog>
  );
};


